import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import snipeHubService from '../../utils/services/snipeHubService';
import { getTeamMeta } from './aflTeams';
import TeamGuernsey from './Guernsey';
import MarketMatrix from './MarketMatrix';
import TeamCBATable from '../PropTools/AFL/TeamCBATable';
import TeamRuckContestsTable from '../PropTools/AFL/TeamRuckContestsTable';
import TeamKickInsTable from '../PropTools/AFL/TeamKickInsTable';
import GameMarketOverviewTable from './GameMarketOverviewTable';
import MatchPriceMovementFeed from './MatchPriceMovementFeed';

// The breakdown tables available on the match page. Rendered one at a time
// via the selector chips; add new entries here as more breakdowns land.
const MATCH_TABLES = [
    {
        id: 'markets',
        label: 'Market Availability',
        icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
    },
    {
        id: 'cba',
        label: 'Centre Bounce Attendance',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
    {
        id: 'ruck',
        label: 'Ruck Contests',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
    {
        id: 'kickins',
        label: 'Kick-Ins',
        icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
    },
];

// Remembers each match page's breakdown selection + player filter for the SPA
// session so navigating away (e.g. into the Prop Tool) and back restores it.
const matchPageStateCache = new Map();

function formatKickoff(match) {
    let date;
    let hasTime = false;
    if (match?.kickoff_time) {
        date = new Date(match.kickoff_time);
        hasTime = true;
    } else if (match?.start_time) {
        // Parse date-only values at local noon to avoid TZ roll-over.
        date = new Date(`${match.start_time}T12:00:00`);
    } else {
        return null;
    }
    if (Number.isNaN(date.getTime())) return null;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((target - today) / 86400000);

    const weekday = date.toLocaleDateString([], { weekday: 'long' });
    const dayMonth = date.toLocaleDateString([], { day: 'numeric', month: 'long' });
    const time = hasTime ? date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;

    let relative = null;
    if (diffDays === 0) relative = 'Today';
    else if (diffDays === 1) relative = 'Tomorrow';
    else if (diffDays > 1 && diffDays < 7) relative = `In ${diffDays} days`;

    return { weekday, dayMonth, time, relative };
}

function TeamBlock({ name, side }) {
    return (
        <div className="flex flex-col items-center gap-2.5 flex-1 min-w-0">
            <TeamGuernsey
                name={name}
                className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 drop-shadow-[0_4px_12px_rgba(0,0,0,0.55)]"
            />
            <div className="text-center min-w-0 w-full">
                <div className="text-base md:text-xl font-bold text-white leading-tight break-words tracking-tight">{name}</div>
                <div className="text-[10px] font-mono text-slate-500 uppercase tracking-wider mt-1">{side}</div>
            </div>
        </div>
    );
}

function SnipeHubMatch() {
    const { matchId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    // Restore the selected breakdown / player filter from a previous visit so
    // returning (e.g. after a Prop Tool detour) doesn't reset the page.
    const cachedPage = matchPageStateCache.get(matchId);

    // The match is normally handed to us by the Snipe Hub card via navigation
    // state. On a hard refresh / direct link that state is gone, so fall back
    // to re-fetching the round and locating the match by id.
    const [match, setMatch] = useState(location.state?.match || null);
    const [roundLabel, setRoundLabel] = useState(location.state?.round || null);
    const [isLoading, setIsLoading] = useState(!location.state?.match);
    const [error, setError] = useState(null);
    const [activeTable, setActiveTable] = useState(cachedPage?.activeTable ?? MATCH_TABLES[0].id);
    // Player filter shared with the Game Market Overview table so the CBA /
    // Ruck / Kick-In tables can jump straight to a player's markets.
    const [marketPlayerFilter, setMarketPlayerFilter] = useState(cachedPage?.marketPlayerFilter ?? '');
    // One-shot request to switch the overview's market chip (e.g. a "find
    // markets" jump from the price feed carries the movement's category).
    const [marketCategoryRequest, setMarketCategoryRequest] = useState(null);
    const overviewRef = useRef(null);

    // Keep the breakdown selection and player filter alive for the SPA session.
    useEffect(() => {
        if (!matchId) return;
        matchPageStateCache.set(matchId, { activeTable, marketPlayerFilter });
    }, [matchId, activeTable, marketPlayerFilter]);

    const handleFindMarkets = useCallback((playerName, marketCategory) => {
        setMarketPlayerFilter(playerName || '');
        if (marketCategory) {
            setMarketCategoryRequest({ category: marketCategory, ts: Date.now() });
        }
        // Let the filter apply, then bring the board into view.
        requestAnimationFrame(() => {
            overviewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
    }, []);

    useEffect(() => {
        if (match) return;
        let cancelled = false;

        (async () => {
            setIsLoading(true);
            const result = await snipeHubService.getAflMatch(matchId);
            if (cancelled) return;

            if (result.success) {
                const found = result.data?.matches?.[0];
                if (found) {
                    setMatch(found);
                    setRoundLabel(result.data?.round || null);
                } else {
                    setError({ message: 'This match could not be found.' });
                }
            } else {
                setError(result);
            }
            setIsLoading(false);
        })();

        return () => { cancelled = true; };
    }, [match, matchId]);

    const homeMeta = useMemo(() => getTeamMeta(match?.home_team), [match]);
    const awayMeta = useMemo(() => getTeamMeta(match?.away_team), [match]);
    const dateInfo = useMemo(() => (match ? formatKickoff(match) : null), [match]);
    const backToHub = () => navigate('/snipe-hub');

    if (isLoading) {
        return (
            <div className="min-h-[calc(100vh-72px)] w-full overflow-y-auto styled-scrollbar">
                <div className="relative max-w-[1720px] mx-auto px-3 md:px-5 py-4 md:py-6">
                    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 overflow-hidden animate-pulse mb-4">
                        <div className="h-1 w-full bg-slate-800" />
                        <div className="px-5 py-8">
                            <div className="flex items-center justify-between gap-4 max-w-xl mx-auto">
                                <div className="flex flex-col items-center gap-2.5 flex-1">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-800" />
                                    <div className="h-4 w-24 bg-slate-800 rounded" />
                                </div>
                                <div className="h-4 w-8 bg-slate-800 rounded" />
                                <div className="flex flex-col items-center gap-2.5 flex-1">
                                    <div className="w-20 h-20 rounded-2xl bg-slate-800" />
                                    <div className="h-4 w-24 bg-slate-800 rounded" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/40 h-64 animate-pulse" />
                </div>
            </div>
        );
    }

    if (error || !match) {
        return (
            <div className="min-h-[calc(100vh-72px)] w-full flex items-center justify-center px-6">
                <div className="flex flex-col items-center text-center max-w-sm">
                    <div className="w-12 h-12 rounded-lg bg-slate-500/5 border border-slate-500/10 flex items-center justify-center mb-4">
                        <svg className="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h4 className="text-sm font-semibold text-slate-300 mb-1.5">Match unavailable</h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                        {error?.message || 'We could not load this match right now.'}
                    </p>
                    <button
                        onClick={backToHub}
                        className="mt-5 px-4 py-2 text-xs font-mono font-medium text-slate-300 border border-slate-700 rounded-lg hover:bg-slate-800 hover:border-slate-600 transition-all"
                    >
                        Back to Snipe Hub
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[calc(100vh-72px)] w-full overflow-y-auto styled-scrollbar">
            <div className="relative max-w-[1720px] mx-auto px-3 md:px-5 py-4 md:py-6">

                {/* Ambient backdrop */}
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                    <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-emerald-500/5 blur-3xl" />
                    <div className="absolute top-32 -left-24 w-72 h-72 rounded-full bg-teal-500/5 blur-3xl" />
                </div>

                {/* Breadcrumb / back */}
                <div className="relative flex items-center gap-2 mb-3.5 px-0.5">
                    <button
                        onClick={backToHub}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono font-medium text-slate-400 border border-slate-700/70 rounded-lg hover:bg-slate-800/60 hover:text-slate-200 hover:border-slate-600 transition-all"
                    >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Snipe Hub
                    </button>
                    {roundLabel && (
                        <span className="text-[11px] font-mono text-slate-600">
                            / <span className="text-slate-500">{roundLabel}</span>
                        </span>
                    )}
                </div>

                {/* Match banner */}
                <header className="relative rounded-2xl border border-white/[0.07] overflow-hidden mb-4 hub-match-reveal shadow-[0_10px_40px_-12px_rgba(0,0,0,0.6)]">
                    {/* Brand accent bar */}
                    <div
                        className="h-1 w-full opacity-90"
                        style={{ background: `linear-gradient(90deg, ${homeMeta.to}, ${awayMeta.to})` }}
                    />

                    {/* Layered atmosphere — tinted toward each club colour */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#0b1322] via-[#0a0f1b] to-[#06090f]" />
                    <div
                        className="absolute -top-20 -left-16 w-96 h-64 rounded-full blur-3xl pointer-events-none opacity-[0.14]"
                        style={{ background: homeMeta.to }}
                    />
                    <div
                        className="absolute -bottom-24 -right-16 w-96 h-64 rounded-full blur-3xl pointer-events-none opacity-[0.14]"
                        style={{ background: awayMeta.to }}
                    />
                    <div
                        className="absolute inset-0 opacity-[0.04] pointer-events-none"
                        style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '44px 100%' }}
                    />

                    <div className="relative px-4 md:px-8 py-5 md:py-7">
                        {/* Meta row */}
                        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 mb-5">
                            <div className="flex items-center gap-1.5 text-[11px] font-mono text-slate-400">
                                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="uppercase tracking-wider">{match.venue || 'Venue TBC'}</span>
                            </div>
                            {dateInfo ? (
                                <>
                                    <span className="text-slate-700">·</span>
                                    <span className="text-[11px] font-mono text-slate-400">
                                        {dateInfo.weekday} {dateInfo.dayMonth}
                                        {dateInfo.time && <span className="text-slate-500"> · {dateInfo.time}</span>}
                                    </span>
                                    {dateInfo.relative && (
                                        <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-mono font-semibold">
                                            {dateInfo.relative}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span className="text-slate-700">·</span>
                                    <span className="text-[10px] font-mono text-slate-600 uppercase tracking-wider">Date TBC</span>
                                </>
                            )}
                        </div>

                        {/* Teams */}
                        <div className="flex items-center justify-between gap-3 max-w-2xl mx-auto">
                            <TeamBlock name={match.home_team} side="Home" />
                            <div className="flex flex-col items-center gap-1.5 px-1 md:px-4">
                                <span className="text-xs md:text-sm font-mono font-bold text-slate-600 tracking-widest">VS</span>
                                <div className="w-10 md:w-14 h-px bg-gradient-to-r from-transparent via-slate-600 to-transparent" />
                            </div>
                            <TeamBlock name={match.away_team} side="Away" />
                        </div>
                    </div>
                </header>

                {/* Table selector — a single dropdown chip on mobile, the full
                    chip row from md up. One breakdown rendered at a time. */}
                <div className="relative hub-match-reveal mb-3.5 px-0.5" style={{ animationDelay: '90ms' }}>
                    {/* Mobile: single dropdown chip */}
                    <div className="relative w-full md:hidden">
                        <select
                            value={activeTable}
                            onChange={(e) => setActiveTable(e.target.value)}
                            aria-label="Select breakdown table"
                            className="w-full appearance-none rounded-lg border border-emerald-500/40 bg-emerald-500/15 py-2 pl-3 pr-9 text-[12px] font-mono font-semibold text-emerald-300 focus:outline-none"
                        >
                            {MATCH_TABLES.map((t) => (
                                <option key={t.id} value={t.id} className="bg-slate-900 text-slate-200">{t.label}</option>
                            ))}
                        </select>
                        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-emerald-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    {/* Desktop: chip row */}
                    <div className="hidden md:flex flex-wrap items-center gap-2">
                        {MATCH_TABLES.map((t) => {
                            const active = t.id === activeTable;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => setActiveTable(t.id)}
                                    className={`flex shrink-0 items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-mono font-semibold border whitespace-nowrap transition-all ${
                                        active
                                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40'
                                            : 'bg-slate-900/50 text-slate-400 border-slate-700/60 hover:text-slate-200 hover:border-slate-600'
                                    }`}
                                >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.75" d={t.icon} />
                                    </svg>
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Active breakdown table. Both stay mounted (hidden via CSS)
                    so switching chips doesn't refetch — the CBA call in
                    particular counts against the prop tool rate limit. */}
                <div className={activeTable === 'markets' ? 'relative hub-match-reveal' : 'hidden'}>
                    <MarketMatrix matchId={match.match_id} />
                </div>
                <div className={activeTable === 'cba' ? 'relative hub-match-reveal' : 'hidden'}>
                    <TeamCBATable teams={[match.home_team, match.away_team]} onFindMarkets={handleFindMarkets} />
                </div>
                <div className={activeTable === 'ruck' ? 'relative hub-match-reveal' : 'hidden'}>
                    <TeamRuckContestsTable teams={[match.home_team, match.away_team]} onFindMarkets={handleFindMarkets} />
                </div>
                <div className={activeTable === 'kickins' ? 'relative hub-match-reveal' : 'hidden'}>
                    <TeamKickInsTable teams={[match.home_team, match.away_team]} onFindMarkets={handleFindMarkets} />
                </div>

                {/* Full odds board — every market line across every bookmaker */}
                <div ref={overviewRef} className="relative hub-match-reveal mt-4 scroll-mt-4" style={{ animationDelay: '160ms' }}>
                    <GameMarketOverviewTable
                        matchId={match.match_id}
                        playerSearch={marketPlayerFilter}
                        onPlayerSearchChange={setMarketPlayerFilter}
                        marketCategoryRequest={marketCategoryRequest}
                    />
                </div>
            </div>

            {/* Floating live price-movement feed (portaled drawer) */}
            <MatchPriceMovementFeed matchId={match.match_id} onFindMarkets={handleFindMarkets} />

            <style jsx>{`
                @keyframes hubMatchReveal {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .hub-match-reveal {
                    animation: hubMatchReveal 0.4s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
                @media (prefers-reduced-motion: reduce) {
                    .hub-match-reveal { animation: none; }
                }
            `}</style>
        </div>
    );
}

export default SnipeHubMatch;
